import { differenceInDays, differenceInHours } from "date-fns";
import { P, match } from "ts-pattern";
import { CalendarIcon } from "@heroicons/react/20/solid";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import classNames from "classnames";
import classes from "./classes.module.scss";

type IProps = {
	startsAt: Date;
	endsAt: Date;
};

enum EActionProgressTagType {
	PENDING = "pending",
	IN_PROGRESS = "in_progress",
	ENDED = "ended",
}

export default function ActionProgressTag(props: IProps) {
	return (
		<div className={classNames(classes["root"], classes[getActionProgressTagType(props)])}>
			<CalendarIcon className={classes["icon"]} />
			<Typography type="p" size="small" weight="medium" className={classes["text"]}>
				{getActionProgressTagText(props)}
			</Typography>
		</div>
	);
}

function getActionProgressTagType(props: Pick<IProps, "startsAt" | "endsAt">): EActionProgressTagType {
	return match(new Date())
		.when(
			() => props.startsAt > props.endsAt,
			() => EActionProgressTagType.ENDED,
		)
		.when(
			(now) => props.startsAt > now,
			() => EActionProgressTagType.PENDING,
		)
		.when(
			(now) => props.endsAt < now,
			() => EActionProgressTagType.ENDED,
		)
		.otherwise(() => EActionProgressTagType.IN_PROGRESS);
}

function getActionProgressTagText(props: Pick<IProps, "startsAt" | "endsAt">) {
	return match(getActionProgressTagType(props))
		.with(EActionProgressTagType.ENDED, () => I18n.asset.pages.professional_space.tabs.actions.progress_tags.ended)
		.with(EActionProgressTagType.PENDING, () => I18n.asset.pages.professional_space.tabs.actions.progress_tags.pending)
		.with(EActionProgressTagType.IN_PROGRESS, () => {
			const daysLeft = differenceInDays(props.endsAt, new Date());
			const hoursLeft = differenceInHours(props.endsAt, new Date());
			const plurality = match([daysLeft, hoursLeft])
				.with([0, 0], () => "less_than_one_hour" as const)
				.with([0, 1], () => "single_hour_left" as const)
				.with([0, P._], () => "several_hours_left" as const)
				.with([1, P._], () => "single_day_left" as const)
				.with([P._, P._], () => "several_days_left" as const)
				.exhaustive();

			return I18n.trslt(I18n.asset.pages.professional_space.tabs.actions.progress_tags.in_progress[plurality], {
				n: daysLeft === 0 ? hoursLeft.toFixed() : daysLeft.toFixed(),
			});
		})
		.exhaustive();
}
